/**
 * Marker, Symbole und Zahlformate — woertlich aus dem Entwurf uebernommen
 * (`IC`, `ART_FARBE`, `baFarbe`, `baBadge`, `idpBadge`, `statusFarbe`,
 * `artIcon`, `artFarbe`, `zahl`, `geld`).
 *
 * Grundsatz des Entwurfs (4.3): keine gefuellten Pillen. Die Farbe traegt der
 * Text oder ein schmaler Marker links davon — so wirken Rollen-, IDP- und
 * Berechtigungsspalte gleich ruhig.
 */

/** Symbolpfade der Vorlage. */
export const IC: Record<string, string> = {
  stift: 'M4 20h4L20 8l-4-4L4 16zM14 6l4 4',
  karten: 'M12 3l9 4.6-9 4.6-9-4.6zM3 12.4l9 4.6 9-4.6M3 17l9 4.6 9-4.6',
  apps: 'M3 5h18v14H3zM3 9.5h18M6 7.2h.01M8.6 7.2h.01M11.2 7.2h.01',
  ordner: 'M3 7a1 1 0 0 1 1-1h5l2 2h8a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z',
  mail: 'M3 6h18v12H3zM3 7l9 6 9-6',
  cloud: 'M7 18a4 4 0 0 1 0-8 5 5 0 0 1 9.6-1A3.5 3.5 0 0 1 18 18z',
  laptop: 'M3 6h11v8H3zM1.5 17h14M17 8.5h5.5v11H17z',
  warnung: 'M12 3l9.5 17H2.5L12 3zM12 9v5M12 17.2v.1',
  box: 'M3.5 5.5h4.5v4.5H3.5zM3.5 14h4.5v4.5H3.5zM11 7.8h9.5M11 16.2h9.5',
  verlauf: 'M3 12a9 9 0 1 0 3-6.7M3 4v4h4M12 8v4l3 2',
};

export const ART_FARBE: Record<string, string> = {
  Applikation: '--art-app', MobileApp: '--art-mobile', Fileserver: '--art-file',
  Postfach: '--art-mail', Verteiler: '--art-mail', M365: '--art-m365', Hardware: '--art-hw',
  Rolle: '--art-rolle', Person: '--art-person', OE: '--art-oe', Gruppe: '--art-gruppe',
};

const ART_SYMBOL: Record<string, string> = {
  Applikation: 'apps', MobileApp: 'laptop', Fileserver: 'ordner', Postfach: 'mail',
  Verteiler: 'mail', M365: 'cloud', Hardware: 'laptop',
};

export const artFarbe = (typ: string): string => ART_FARBE[typ] ?? '--text-tertiary';
export const artIcon = (typ: string): string => IC[ART_SYMBOL[typ] ?? 'box'] ?? IC['box']!;

export function baFarbe(name: string): string {
  const M: Record<string, string> = {
    'einfacher Zugriff': '--ba-zugriff', 'lesen': '--ba-lesen', 'schreiben': '--ba-schreiben',
    'Adminrecht': '--ba-admin', 'je nach Berechtigungskonzept der Anwendung': '--ba-konzept',
    'individuell': '--ba-individuell',
    'X': '--ba-x', '(X)': '--ba-x', 'Sonstiges': '--ba-sonstiges',
    'nicht berücksichtigt': '--text-tertiary',
  };
  return M[name] ?? '--text-tertiary';
}

/**
 * Berechtigungsarten, fuer die es eine Darstellung gibt — alles Weitere wird
 * neutral gesetzt und als „frei erfasst" kenntlich.
 *
 * **Keine Auskunft ueber den Bestand.** Was wirklich katalogisiert ist,
 * steht in `iam.Aufzaehlung` und wird auf `/richtlinien` gegen `_werte`
 * gehalten; diese Liste entscheidet nur ueber Farbe und Form eines Chips.
 *
 * Vier Werte sind am 27.08.2026 entfallen: `x`,
 * `nach ATOZ-Berechtigungskonzept`, `Apple iPhone` und
 * `TAN-Generator (Areal Bank)`. Sie stammen aus dem Entwurf, kommen im
 * Bestand nicht vor — am 27.08.2026 ueber
 * `/api/zuordnungen/_werte?feld=berechtigungsart` nachgezaehlt — und der
 * letzte trug den Namen eines fremden Hauses. Eine Farbe fuer einen Wert,
 * den es nicht gibt, wird nie nachgeschlagen; die Liste sah aber aus wie
 * ein Katalog und wurde auf `/richtlinien` auch als einer gezeigt.
 */
const BA_KATALOG = new Set([
  'einfacher Zugriff', 'lesen', 'schreiben', 'Adminrecht',
  'je nach Berechtigungskonzept der Anwendung', 'individuell', 'nicht berücksichtigt',
  'X', '(X)', 'Sonstiges',
]);

/**
 * Was von einer Berechtigungsart sichtbar bleibt.
 *
 * `X` und `(X)` stehen in der Standardrollenmatrix an der Hardware-
 * Ausstattung. Sie sind dort kein Zugriffsrecht, sondern ein Kreuz in einer
 * Tabelle: das Geraet gehoert zum Paket und wird mit der Rolle automatisch
 * zugewiesen. Als Chip gesetzt las es sich wie eine eigene Berechtigungsart.
 * Der Kunde am 22.09.2026: „einfach nichts hinschreiben".
 *
 * Die Werte bleiben im Bestand und in den Wertelisten — nur die Anzeige
 * schweigt.
 */
export function baAnzeige(name: string | null | undefined): string {
  return !name || name === 'X' || name === '(X)' ? '' : name;
}

export function baBadge(name: string): string {
  const bekannt = BA_KATALOG.has(name);
  // Zeilen aus dem Zielbild-Entwurf des Auftraggebers stehen technisch, nicht als Wort
  const zielbild = /^(Benutzer|d:|isb@|GPU)/.test(name);
  const f = 'var(' + baFarbe(name) + ')';

  if (name === 'nicht berücksichtigt') {
    return 'display:inline-block;font-size:12px;color:var(--text-tertiary);text-decoration:line-through;' +
      'box-shadow:inset 3px 0 0 var(--border-subtle);padding-left:9px;overflow-wrap:anywhere';
  }
  if (name === '(X)') {
    return 'display:inline-block;font-size:12px;color:' + f +
      ';box-shadow:inset 3px 0 0 ' + f + ';padding-left:9px;white-space:nowrap';
  }
  if (zielbild || !bekannt) {
    return 'display:inline-block;font-family:var(--mono);font-size:12px;color:var(--text-secondary);' +
      'box-shadow:inset 3px 0 0 var(--border-default);padding-left:9px;overflow-wrap:anywhere';
  }
  /* **Kein `white-space:nowrap`.**
   *
   * Der Katalog fuehrt eine Berechtigungsart mit 41 Zeichen — „je nach
   * Berechtigungskonzept der Anwendung". Sie steht in der Rollendetailansicht
   * in einer Rasterspalte von hoechstens 150 px und in der Rollenmatrix in
   * einer Spalte von 240 px. Mit `nowrap` wuchs die Marke auf ihre volle
   * Breite von rund 250 px, verliess ihre Zelle und wurde **ueber** die
   * Nachbarspalte gezeichnet: der Gruppenname lag unter dem Wortlaut der
   * Berechtigungsart, beide unlesbar. Gemeldet am 28.08.2026 mit
   * Bildschirmfoto.
   *
   * Ein Ueberlauf in die Nachbarspalte ist kein Schoenheitsfehler, sondern
   * eine falsche Auskunft: was dort steht, gehoert sichtbar zu einer anderen
   * Spalte. Die Marke bricht deshalb um. Kurze Werte („lesen", „schreiben")
   * fuellen die Spalte ohnehin nicht aus und bleiben einzeilig; der
   * Farbstreifen links waechst bei zwei Zeilen mit, das ist richtig so —
   * er markiert die Zeile, nicht eine Textzeile. */
  return 'display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:500;' +
    'color:var(--text-primary);overflow-wrap:anywhere;box-shadow:inset 3px 0 0 ' + f +
    ';padding-left:9px';
}

export function idpBadge(idp: string): string {
  const F: Record<string, string> = {
    Entra: '--art-m365', AD: '--art-gruppe', SAP: '--art-app', lokal: '--text-tertiary',
  };
  const f = 'var(' + (F[idp] ?? '--text-tertiary') + ')';
  return 'display:inline-block;font-size:12px;font-weight:500;color:' + f + ';white-space:nowrap';
}

export function statusFarbe(s: string): string {
  const M: Record<string, string> = {
    aktiv: '--success', 'in Prüfung': '--warning', entwurf: '--info', inaktiv: '--text-tertiary',
    beantragt: '--warning', entwertet: '--text-tertiary',
  };
  return M[s] ?? '--text-tertiary';
}

export function zahl(v: number | null | undefined, dez = 0): string {
  if (v == null || Number.isNaN(v)) return '—';
  return Number(v).toLocaleString('de-DE', {
    minimumFractionDigits: dez, maximumFractionDigits: dez,
  });
}

const DATUMFORMAT = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit', month: '2-digit', year: 'numeric',
});

/**
 * Ein Wert oder ein Strich — und `''` zaehlt als leer.
 *
 * ## Warum `?? '—'` nicht reicht
 *
 * `??` faengt `null` und `undefined`. Es faengt **nicht** die leere
 * Zeichenkette, und `'' ?? '—'` ist `''`. Die Zelle bleibt dann leer.
 *
 * Das ist kein theoretischer Fall: `werkzeuge/matrix-lesen.py` legt Felder,
 * die die Standardrollenmatrix nicht fuehrt, ausdruecklich als `''` an —
 * `kritikalitaet` an allen Rollen und Assets, `idp` an allen Assets, und der
 * Rollentyp steht bei allen 321 Rollen des Kundenbestands leer. Wer `?? '—'`
 * schreibt, bekommt genau dort nichts zu sehen, wo er den Strich haben
 * wollte.
 *
 * **Und der Unterschied ist eine Aussage:** eine leere Zelle liest sich als
 * „vergessen einzutragen", ein Strich als „nicht erfasst". Das eine ist ein
 * Vorwurf an die Pflege, das andere eine Eigenschaft der Quelle.
 *
 * `0` und `false` bleiben stehen — sie sind Werte, keine Luecken. Deshalb
 * nicht `wert || '—'`.
 */
export function oderStrich(wert: unknown): string {
  if (wert === null || wert === undefined) return '—';
  const text = String(wert).trim();
  return text === '' ? '—' : text;
}

export function datum(v: string | null | undefined): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : DATUMFORMAT.format(d);
}

/** Initialen aus einem Namen — zwei Buchstaben, wie im Entwurf. */
export function initialen(name: string | null | undefined): string {
  return String(name ?? '')
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** Betraege stehen anwendungsweit ungekuerzt in Euro — keine T€-Abkuerzung. */
export function geld(v: number | null | undefined): string {
  if (v == null) return '—';
  return zahl(v, 2) + ' €';
}

/* ------------------------------------------------------- Abkuerzungen */

/**
 * Kuerzel, die in Spaltenkoepfen und Feldbeschriftungen stehen — und die
 * niemand kennen muss.
 *
 * Der Kunde hat beim Test am 24.08.2026 gefragt, wofuer „IDP" und „OE"
 * stehen. Beide sind in diesem Werkzeug alltaeglich und ausserhalb nicht;
 * eine Spalte, deren Kopf man nachschlagen muss, ist eine Spalte, die man
 * uebergeht.
 *
 * Ausgeschrieben werden sie **nicht**: „Organisationseinheit" sprengt jede
 * Spaltenbreite, und die Kuerzel stehen so in der Mappe des Kunden. Sie
 * bekommen stattdessen eine Erklaerung, die beim Zeigen erscheint.
 *
 * `title` ist dabei ein schwaches Mittel — ohne Zeigegeraet erscheint es
 * nicht. Es ist hier trotzdem das richtige: die Erklaerung ist Beiwerk, und
 * ein Kopf, der sie dauerhaft trägt, waere zwei Zeilen hoch.
 */
export const ABKUERZUNGEN: Record<string, string> = {
  'OE': 'Organisationseinheit — Bereich, Abteilung oder Team.',
  'IDP': 'Identity Provider — das System, das die Anmeldung führt, etwa Entra ID oder das lokale Active Directory.',
  'SoD': 'Segregation of Duties, Funktionstrennung — zwei Berechtigungen, die niemand zugleich haben soll.',
  'JML': 'Joiner, Mover, Leaver — Eintritt, Wechsel und Austritt einer Person.',
  'IAM': 'Identity and Access Management — Verwaltung von Identitäten und Zugriffen.',
  'M365': 'Microsoft 365.',
  'AD': 'Active Directory — das lokale Verzeichnis.',
  'UPN': 'User Principal Name — die Anmeldekennung, meist in Form einer E-Mail-Adresse.',
  'RBAC': 'Role Based Access Control — Rechtevergabe über Rollen statt einzeln je Person.',
  'SBOM': 'Software Bill of Materials — Verzeichnis der eingesetzten Fremdbestandteile.',
};

/**
 * Erklaerung zu einer Beschriftung, oder `undefined`.
 *
 * Gesucht wird nach **ganzen Woertern**: „Owner" darf nicht anschlagen, weil
 * „OE" darin vorkommt. Stehen mehrere Kuerzel in einer Beschriftung, werden
 * ihre Erklaerungen aneinandergereiht.
 */
export function erklaerung(beschriftung: string): string | undefined {
  const woerter = new Set(beschriftung.split(/[^\p{L}\p{N}]+/u).filter(Boolean));
  const treffer = Object.keys(ABKUERZUNGEN).filter((k) => woerter.has(k));
  if (treffer.length === 0) return undefined;
  return treffer.map((k) => ABKUERZUNGEN[k]).join(' · ');
}
